import React, { useState, useEffect, useMemo } from 'react';
import {
  Shield, ShieldAlert, ShieldCheck, KeyRound, Lock, Unlock, RefreshCw,
  AlertTriangle, Eye, EyeOff, Users, Clock, Activity, Globe, Hash,
  CheckCircle, XCircle, LogOut, Download, Search, Filter, ChevronDown,
  AlertCircle, Fingerprint, RotateCcw, Trash2, Copy, X, Wrench, Radio,
  Bell, Calendar, Send, Plus, ToggleLeft, ToggleRight, ExternalLink,
  UserCheck, UserPlus, UserMinus, User, FileText, Layers
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import ThreatMap from '../../components/security/ThreatMap';
import ApprovalsPanel from '../../components/security/ApprovalsPanel';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SecurityManagementPage = ({ getAuthHeaders, currentUser }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lockedAccounts, setLockedAccounts] = useState([]);
  const [currentCode, setCurrentCode] = useState(null);
  const [showCode, setShowCode] = useState(false);
  const [showChangeCode, setShowChangeCode] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showForceReset, setShowForceReset] = useState(false);
  const [showInvalidateSessions, setShowInvalidateSessions] = useState(false);
  const [filterEvent, setFilterEvent] = useState('all');
  const [searchAudit, setSearchAudit] = useState('');

  const [lockdownEnabled, setLockdownEnabled] = useState(false);
  const [lockdownReason, setLockdownReason] = useState('');
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [protection, setProtection] = useState({ two_fa_enabled: false, login_notifications: true, ip_whitelist: [] });
  const [showLockdownConfirm, setShowLockdownConfirm] = useState(false);
  const [staffActivity, setStaffActivity] = useState([]);
  const [threatSummary, setThreatSummary] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [severityFilter, setSeverityFilter] = useState('all');

  // Site Management state
  const [siteMode, setSiteMode] = useState({ enabled: false, title: '', message: '', scheduled_end: null, allow_browse: false });
  const [siteAlerts, setSiteAlerts] = useState([]);
  const [scheduledMaint, setScheduledMaint] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [siteSubTab, setSiteSubTab] = useState('maintenance');
  // Forms
  const [newAlert, setNewAlert] = useState({ message: '', type: 'warning', dismissible: true, link_text: '', link_url: '' });
  const [newSchedule, setNewSchedule] = useState({ title: '', description: '', scheduled_start: '', scheduled_end: '', auto_activate: true, full_lockdown: true });
  const [newIncident, setNewIncident] = useState({ title: '', description: '', severity: 'minor', status: 'investigating', affected_services: [] });

  // Security Access Authorization
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [selectedGrantUser, setSelectedGrantUser] = useState('');
  const [incidentUpdate, setIncidentUpdate] = useState({ id: null, status: '', update_message: '' });

  // NEW: Threats + Step-Up auth + Emergency Lockdown
  const [threats, setThreats] = useState({ blocked: [], suspicious: [], totals: {} });
  // Forensics — IP intelligence + emergency access code
  const [ipIntel, setIpIntel] = useState([]);
  const [forensicEvents, setForensicEvents] = useState([]);
  const [forensicFilter, setForensicFilter] = useState({ severity: '', hours: 24, action: '', ip: '' });
  const [emergencyCode, setEmergencyCode] = useState({ active: false, used: false, code: null });
  const [lockdownState, setLockdownState] = useState({ enabled: false });
  const [stepUpToken, setStepUpToken] = useState(null);
  const [stepUpExpiresAt, setStepUpExpiresAt] = useState(null);
  const [showStepUp, setShowStepUp] = useState(false);
  const [stepUpPassword, setStepUpPassword] = useState('');
  const [stepUpError, setStepUpError] = useState('');
  const [stepUpProcessing, setStepUpProcessing] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // function to run after step-up succeeds
  const [modeForm, setModeForm] = useState({ title: 'Scheduled Maintenance', message: "We're performing scheduled maintenance. We'll be back shortly.", scheduled_end: '', allow_browse: false, scope: 'all' });

  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isExecutive = ['superadmin', 'executive'].includes(currentUser?.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const [auditRes, lockedRes, alertsRes, sessionsRes, lockdownRes, protectionRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/audit-log?limit=200`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/locked-accounts`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/security/alerts`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/security/sessions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/security/lockdown`, { headers }).catch(() => ({ data: { enabled: false } })),
        axios.get(`${BACKEND_URL}/api/admin/security/account-protection`, { headers }).catch(() => ({ data: {} })),
      ]);
      setAuditLog(auditRes.data || []);
      setLockedAccounts(lockedRes.data || []);
      setSecurityAlerts(alertsRes.data || []);
      setActiveSessions(sessionsRes.data || []);
      setLockdownEnabled(lockdownRes.data?.enabled || false);
      setLockdownReason(lockdownRes.data?.reason || '');
      setProtection(protectionRes.data || { two_fa_enabled: false, login_notifications: true, ip_whitelist: [] });

      if (isSuperAdmin) {
        try {
          const codeRes = await axios.get(`${BACKEND_URL}/api/admin/security-code/current`, { headers });
          setCurrentCode(codeRes.data);
        } catch {}
        // Fetch authorized users and all staff
        try {
          const [authRes, staffRes, threatRes, auditRes] = await Promise.all([
            axios.get(`${BACKEND_URL}/api/admin/security/authorized-users`, { headers }).catch(() => ({ data: { users: [] } })),
            axios.get(`${BACKEND_URL}/api/admin/staff-accounts`, { headers }).catch(() => ({ data: [] })),
            axios.get(`${BACKEND_URL}/api/admin/security/threat-summary`, { headers }).catch(() => ({ data: null })),
            axios.get(`${BACKEND_URL}/api/admin/security/audit-logs?limit=20`, { headers }).catch(() => ({ data: [] }))
          ]);
          setAuthorizedUsers(authRes.data?.users || []);
          setAllStaff((staffRes.data || []).filter(s => s.role !== 'superadmin'));
          setThreatSummary(threatRes.data);
          setAuditLogs(auditRes.data || []);
        } catch {}
      }
    } finally {
      setLoading(false);
    }
  };

  // ==================== Site Management Handlers ====================
  const fetchSiteData = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    const [modeRes, alertsRes, schedRes, incRes] = await Promise.all([
      axios.get(`${BACKEND_URL}/api/maintenance/admin/mode`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${BACKEND_URL}/api/maintenance/admin/alerts`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/maintenance/admin/schedule`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/maintenance/admin/incidents`, { headers }).catch(() => ({ data: [] })),
    ]);
    setSiteMode(modeRes.data || {});
    setSiteAlerts(alertsRes.data || []);
    setScheduledMaint(schedRes.data || []);
    setIncidents(incRes.data || []);
    setModeForm(prev => ({
      ...prev,
      title: modeRes.data?.title || prev.title,
      message: modeRes.data?.message || prev.message,
      scheduled_end: modeRes.data?.scheduled_end || '',
      allow_browse: modeRes.data?.allow_browse || false,
      scope: modeRes.data?.scope || 'all',
    }));
  };

  useEffect(() => { if (activeTab === 'site') fetchSiteData(); }, [activeTab]);

  const toggleMaintenanceMode = async (enabled) => {
    const headers = getAuthHeaders();
    if (!headers) return;
    await axios.post(`${BACKEND_URL}/api/maintenance/admin/mode`, {
      enabled, title: modeForm.title, message: modeForm.message,
      scheduled_end: modeForm.scheduled_end || null, allow_browse: modeForm.allow_browse,
      scope: modeForm.scope || 'all',
    }, { headers });
    const scopeLabel = modeForm.scope === 'staff' ? ' (staff portal only)' : modeForm.scope === 'public' ? ' (public site only)' : '';
    toast.success(enabled ? `Maintenance mode activated${scopeLabel}` : 'Maintenance mode deactivated');
    fetchSiteData();
  };

  // ===== NEW: Step-Up Auth + Threats + Emergency Lockdown helpers =====

  const isStepUpValid = () => stepUpToken && stepUpExpiresAt && Date.now() < stepUpExpiresAt;

  const stepUpHeaders = () => {
    const base = getAuthHeaders() || {};
    if (isStepUpValid()) base['X-Security-StepUp'] = stepUpToken;
    return base;
  };

  // Run an action — if step-up is missing or expired, open modal first then resume
  const withStepUp = (action, label = 'this action') => {
    if (isStepUpValid()) {
      action();
    } else {
      setPendingAction(() => action);
      setStepUpError(`Re-verify your password to perform ${label}.`);
      setShowStepUp(true);
    }
  };

  const submitStepUp = async (e) => {
    if (e) e.preventDefault();
    if (!stepUpPassword) { setStepUpError('Password is required'); return; }
    setStepUpProcessing(true);
    setStepUpError('');
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/security/step-up`,
        { password: stepUpPassword }, { headers });
      setStepUpToken(res.data.token);
      setStepUpExpiresAt(Date.now() + (res.data.expires_in - 5) * 1000);
      setStepUpPassword('');
      setShowStepUp(false);
      toast.success('Identity verified — destructive actions unlocked for 10 minutes');
      if (pendingAction) {
        const fn = pendingAction; setPendingAction(null);
        setTimeout(fn, 100);
      }
    } catch (err) {
      setStepUpError(err.response?.data?.detail || 'Re-verification failed');
    } finally {
      setStepUpProcessing(false);
    }
  };

  const fetchThreats = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const [t, l] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/security/threats`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/security/lockdown-status`, { headers }),
      ]);
      setThreats(t.data || { blocked: [], suspicious: [], totals: {} });
      setLockdownState(l.data || { enabled: false });
    } catch {}
  };

  useEffect(() => { if (activeTab === 'threats') fetchThreats(); }, [activeTab]);

  const fetchForensics = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    const params = new URLSearchParams();
    if (forensicFilter.severity) params.append('severity', forensicFilter.severity);
    if (forensicFilter.hours) params.append('hours', String(forensicFilter.hours));
    if (forensicFilter.action) params.append('action_contains', forensicFilter.action);
    if (forensicFilter.ip) params.append('ip', forensicFilter.ip);
    params.append('limit', '200');
    // Each request is fire-and-forget-independently — one 404 must not nuke the others.
    // Previously we used Promise.all which meant a missing emergency-code endpoint on a
    // stale production build left the whole Forensics tab looking "not loading".
    const [f, i, e] = await Promise.allSettled([
      axios.get(`${BACKEND_URL}/api/admin/security/audit-log/forensic?${params.toString()}`, { headers }),
      axios.get(`${BACKEND_URL}/api/admin/security/ip-intel?hours=${forensicFilter.hours || 24}`, { headers }),
      axios.get(`${BACKEND_URL}/api/admin/security/emergency-code/status`, { headers }),
    ]);
    let anyFailed = false;
    if (f.status === 'fulfilled') setForensicEvents(f.value.data.events || []);
    else { anyFailed = true; console.warn('forensic audit-log failed', f.reason?.response?.status); }
    if (i.status === 'fulfilled') setIpIntel(i.value.data.ips || []);
    else { anyFailed = true; console.warn('forensic ip-intel failed', i.reason?.response?.status); }
    if (e.status === 'fulfilled') setEmergencyCode(prev => ({ ...prev, ...e.value.data }));
    else console.warn('emergency-code status failed (non-fatal)', e.reason?.response?.status);
    if (anyFailed && f.status === 'rejected' && i.status === 'rejected') {
      // Only complain if BOTH primary data sources went down.
      toast.error('Failed to load forensics — check network / permissions');
    }
  };
  useEffect(() => { if (activeTab === 'forensics') fetchForensics(); /* eslint-disable-next-line */ }, [activeTab, forensicFilter.hours, forensicFilter.severity]);

  const generateEmergencyCode = () => withStepUp(async () => {
    if (!window.confirm('Generate a NEW single-use Emergency Access Code (30-min lifespan)? It will bypass the security gate for one login only.')) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/security/emergency-code/generate`, {}, { headers: stepUpHeaders() });
      setEmergencyCode({ active: true, used: false, code: res.data.code, expires_at: res.data.expires_at });
      toast.success('Emergency code issued — share ONLY via a secure channel');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    }
  }, 'generating an emergency code');

  const revokeEmergencyCode = () => withStepUp(async () => {
    if (!window.confirm('Immediately revoke the current emergency code?')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/security/emergency-code/revoke`, {}, { headers: stepUpHeaders() });
      setEmergencyCode({ active: false, used: true, code: null });
      toast.success('Emergency code revoked');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    }
  }, 'revoking the emergency code');

  const unblockIP = (ip) => withStepUp(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/security/threats/unblock`, { ip }, { headers: stepUpHeaders() });
      toast.success(`Unblocked ${ip}`);
      fetchThreats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unblock failed');
    }
  }, 'unblocking an IP');

  const blockIPManually = (ip, reason) => withStepUp(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/security/threats/block`, { ip, reason }, { headers: stepUpHeaders() });
      toast.success(`Blocked ${ip}`);
      fetchThreats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Block failed');
    }
  }, 'blocking an IP');

  const rotateSecurityCode = () => withStepUp(async () => {
    const input = window.prompt(
      'Enter a NEW security gate code (6-24 chars, A-Z and 0-9 only).\n\nLeave BLANK and press OK to auto-generate a random 8-char code.\nPress Cancel to abort.',
      ''
    );
    if (input === null) return; // cancelled
    const customCode = input.trim().toUpperCase();
    if (customCode && !/^[A-Z0-9]{6,24}$/.test(customCode)) {
      toast.error('Code must be 6-24 chars, A-Z and 0-9 only');
      return;
    }
    if (!window.confirm(
      customCode
        ? `Set the security gate code to "${customCode}"? Anyone using the old code will be locked out immediately. Continue?`
        : 'Auto-generate a NEW security gate code? Anyone using the old code will be locked out immediately. Continue?'
    )) return;
    try {
      const body = customCode ? { custom_code: customCode } : {};
      const res = await axios.post(`${BACKEND_URL}/api/admin/security/rotate-security-code`, body, { headers: stepUpHeaders() });
      toast.success(`New code: ${res.data.new_code} — share with trusted staff only`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rotation failed');
    }
  }, 'rotating the security code');

  const revokeAllSessions = () => withStepUp(async () => {
    if (!window.confirm('Force-logout EVERY staff member? They will need to re-authenticate immediately. Continue?')) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/security/sessions/revoke-all`, {}, { headers: stepUpHeaders() });
      toast.success(`Logged out ${res.data.affected} staff sessions`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    }
  }, 'force-logging-out everyone');

  const toggleEmergencyLockdown = () => withStepUp(async () => {
    const nextState = !lockdownState.enabled;
    const verb = nextState ? 'ENABLE Emergency Lockdown — this will block all further destructive Security Center actions until you disable it' : 'DISABLE Emergency Lockdown';
    if (!window.confirm(`${verb}. Continue?`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/security/emergency-lockdown`,
        { enabled: nextState }, { headers: stepUpHeaders() });
      toast.success(nextState ? '🔒 Emergency Lockdown ACTIVE' : '✓ Lockdown disabled');
      fetchThreats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    }
  }, 'toggling Emergency Lockdown');



  const createAlert = async () => {
    if (!newAlert.message.trim()) { toast.error('Alert message required'); return; }
    const headers = getAuthHeaders();
    await axios.post(`${BACKEND_URL}/api/maintenance/admin/alerts`, newAlert, { headers });
    toast.success('Alert created');
    setNewAlert({ message: '', type: 'warning', dismissible: true, link_text: '', link_url: '' });
    fetchSiteData();
  };

  const toggleAlertActive = async (id) => {
    const headers = getAuthHeaders();
    await axios.put(`${BACKEND_URL}/api/maintenance/admin/alerts/${id}/toggle`, {}, { headers });
    fetchSiteData();
  };

  const deleteAlert = async (id) => {
    const headers = getAuthHeaders();
    await axios.delete(`${BACKEND_URL}/api/maintenance/admin/alerts/${id}`, { headers });
    toast.success('Alert deleted');
    fetchSiteData();
  };

  const createSchedule = async () => {
    if (!newSchedule.title.trim() || !newSchedule.scheduled_start || !newSchedule.scheduled_end) {
      toast.error('Title, start and end times are required'); return;
    }
    const headers = getAuthHeaders();
    await axios.post(`${BACKEND_URL}/api/maintenance/admin/schedule`, newSchedule, { headers });
    toast.success('Maintenance scheduled');
    setNewSchedule({ title: '', description: '', scheduled_start: '', scheduled_end: '', auto_activate: true, full_lockdown: true });
    fetchSiteData();
  };

  const deleteSchedule = async (id) => {
    const headers = getAuthHeaders();
    await axios.delete(`${BACKEND_URL}/api/maintenance/admin/schedule/${id}`, { headers });
    toast.success('Schedule removed');
    fetchSiteData();
  };

  const createIncident = async () => {
    if (!newIncident.title.trim()) { toast.error('Incident title required'); return; }
    const headers = getAuthHeaders();
    await axios.post(`${BACKEND_URL}/api/maintenance/admin/incidents`, newIncident, { headers });
    toast.success('Incident created');
    setNewIncident({ title: '', description: '', severity: 'minor', status: 'investigating', affected_services: [] });
    fetchSiteData();
  };

  const updateIncident = async () => {
    if (!incidentUpdate.id || !incidentUpdate.update_message.trim()) { toast.error('Status and message required'); return; }
    const headers = getAuthHeaders();
    await axios.put(`${BACKEND_URL}/api/maintenance/admin/incidents/${incidentUpdate.id}`, {
      status: incidentUpdate.status, update_message: incidentUpdate.update_message
    }, { headers });
    toast.success('Incident updated');
    setIncidentUpdate({ id: null, status: '', update_message: '' });
    fetchSiteData();
  };

  const deleteIncident = async (id) => {
    const headers = getAuthHeaders();
    await axios.delete(`${BACKEND_URL}/api/maintenance/admin/incidents/${id}`, { headers });
    toast.success('Incident deleted');
    fetchSiteData();
  };

  const handleChangeCode = async () => {
    if (!newCode || newCode.length < 6) {
      toast.error('Security code must be at least 6 characters');
      return;
    }
    if (!confirmPassword) {
      toast.error('Please enter your password to confirm');
      return;
    }
    try {
      const headers = getAuthHeaders();
      await axios.put(`${BACKEND_URL}/api/admin/security-code/update`, {
        new_code: newCode,
        current_password: confirmPassword
      }, { headers });
      toast.success('Security code updated successfully');
      setShowChangeCode(false);
      setNewCode('');
      setConfirmPassword('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update security code');
    }
  };

  const handleUnlockAccount = async (username) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/unlock-account/${username}`, {}, { headers });
      toast.success(`Account '${username}' unlocked`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock account');
    }
  };

  const handleGrantSecurityAccess = async (targetUsername) => {
    if (!targetUsername) return;
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/security/authorize-user?target_username=${targetUsername}&action=grant`, {}, { headers });
      toast.success(`Security access granted to ${targetUsername}`);
      setSelectedGrantUser('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant access');
    }
  };

  const handleRevokeSecurityAccess = async (targetUsername) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/security/authorize-user?target_username=${targetUsername}&action=revoke`, {}, { headers });
      toast.success(`Security access revoked from ${targetUsername}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke access');
    }
  };


  const handleForceResetAll = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/force-password-reset-all`, {}, { headers });
      toast.success(res.data.message);
      setShowForceReset(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to force password reset');
    }
  };

  const handleInvalidateSessions = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/invalidate-all-sessions`, {}, { headers });
      toast.success(res.data.message);
      setShowInvalidateSessions(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invalidate sessions');
    }
  };

  // Stats from audit log
  const stats = useMemo(() => {
    const now = Date.now();
    const last24h = auditLog.filter(e => now - new Date(e.timestamp).getTime() < 86400000);
    return {
      totalEvents: auditLog.length,
      failedLogins: last24h.filter(e => e.event_type === 'login_failed').length,
      successLogins: last24h.filter(e => e.event_type === 'login_success').length,
      codeAttempts: last24h.filter(e => e.event_type === 'security_code_failed').length,
      lockedNow: lockedAccounts.length,
    };
  }, [auditLog, lockedAccounts]);

  const filteredAudit = useMemo(() => {
    let items = [...auditLog];
    if (filterEvent !== 'all') items = items.filter(e => e.event_type === filterEvent);
    if (severityFilter && severityFilter !== 'all') {
      items = items.filter(e => (e.severity || 'info') === severityFilter);
    }
    if (searchAudit) {
      const q = searchAudit.toLowerCase();
      items = items.filter(e =>
        e.event_type?.toLowerCase().includes(q) ||
        e.ip?.toLowerCase().includes(q) ||
        e.username?.toLowerCase().includes(q) ||
        (typeof e.details === 'string' ? e.details.toLowerCase() : JSON.stringify(e.details || {}).toLowerCase()).includes(q)
      );
    }
    return items;
  }, [auditLog, filterEvent, searchAudit, severityFilter]);

  const eventTypes = useMemo(() => {
    const types = new Set(auditLog.map(e => e.event_type).filter(Boolean));
    return Array.from(types).sort();
  }, [auditLog]);

  const getEventIcon = (type) => {
    switch (type) {
      case 'login_success': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'login_failed': return <XCircle className="w-4 h-4 text-red-400" />;
      case 'account_locked': return <Lock className="w-4 h-4 text-amber-400" />;
      case 'security_code_success': return <ShieldCheck className="w-4 h-4 text-emerald-400" />;
      case 'security_code_failed': return <ShieldAlert className="w-4 h-4 text-red-400" />;
      case 'security_code_blocked': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'security_code_changed': return <KeyRound className="w-4 h-4 text-cyan-400" />;
      case 'force_password_reset_all': return <RotateCcw className="w-4 h-4 text-amber-400" />;
      case 'all_sessions_invalidated': return <LogOut className="w-4 h-4 text-red-400" />;
      default: return <Activity className="w-4 h-4 text-slate-400" />;
    }
  };

  const getEventBadgeColor = (type) => {
    if (type.includes('success')) return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    if (type.includes('failed') || type.includes('blocked')) return 'bg-red-500/10 text-red-400 border-red-500/20';
    if (type.includes('locked') || type.includes('reset')) return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
    if (type.includes('changed') || type.includes('invalidated')) return 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20';
    return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
  };

  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const getRelativeTime = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  if (!isExecutive) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="access-denied">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 text-lg">Access restricted to executives and superadmins</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: 'threats', label: 'Threats', icon: ShieldAlert, badge: threats.blocked?.length > 0 ? threats.blocked.length : null },
    { id: 'forensics', label: 'Forensics', icon: Fingerprint, badge: emergencyCode.active ? '!' : null },
    { id: 'lockdown', label: 'Lockdown', icon: Lock },
    { id: 'site', label: 'Site Management', icon: Wrench },
    { id: 'sessions', label: 'Sessions', icon: Globe },
    { id: 'alerts', label: 'Alerts', icon: AlertTriangle, badge: securityAlerts.length > 0 ? securityAlerts.length : null },
    { id: 'audit', label: 'Audit Log', icon: Activity },
    { id: 'access', label: 'Access Control', icon: KeyRound },
  ];

  return (
    <div className="space-y-6" data-testid="security-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/20 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight" data-testid="page-title">Security Centre</h1>
            <p className="text-slate-500 text-sm">Monitor threats, manage access & audit activity</p>
          </div>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-security"
          className="border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-900/60 rounded-xl border border-slate-800/60 w-fit flex-wrap">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}>
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge && <span className="px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold">{tab.badge}</span>}
          </button>
        ))}
      </div>

      {/* Emergency Lockdown banner — shown when active */}
      {lockdownState.enabled && (
        <div className="rounded-xl border-2 border-red-500/50 bg-gradient-to-r from-red-500/10 to-rose-500/5 p-4 flex items-start gap-3 animate-pulse" data-testid="emergency-lockdown-banner">
          <div className="w-10 h-10 rounded-lg bg-red-500/20 border border-red-500/40 flex items-center justify-center flex-shrink-0">
            <Lock className="w-5 h-5 text-red-400" />
          </div>
          <div className="flex-1">
            <p className="text-red-300 font-semibold text-sm">🔒 Emergency Lockdown is ACTIVE</p>
            <p className="text-red-400/80 text-xs mt-0.5">All destructive Security Centre actions are blocked. Only you can disable this from the Threats tab.</p>
          </div>
        </div>
      )}

      {/* Threats Tab */}
      {activeTab === 'forensics' && (
        <div className="space-y-5" data-testid="forensics-tab">
          {/* Emergency Access Code Panel */}
          <div className="rounded-2xl border border-rose-500/25 bg-gradient-to-br from-rose-500/[0.06] to-orange-500/[0.03] p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center flex-shrink-0">
                  <KeyRound className="w-5 h-5 text-rose-300" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-rose-300/70">Break-glass</p>
                  <h3 className="text-white font-semibold text-base">Emergency Access Code</h3>
                  <p className="text-slate-400 text-xs mt-1 max-w-md">Single-use 10-char code that bypasses the security gate for 30 minutes. Use when the primary code is compromised or a superadmin is locked out.</p>
                </div>
              </div>
              <div className="flex gap-2">
                {emergencyCode.active ? (
                  <>
                    <Button onClick={revokeEmergencyCode} size="sm" data-testid="revoke-emergency-code"
                      className="bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white">
                      <X className="w-3.5 h-3.5 mr-1.5" /> Revoke
                    </Button>
                  </>
                ) : (
                  <Button onClick={generateEmergencyCode} size="sm" data-testid="generate-emergency-code"
                    className="bg-gradient-to-r from-rose-500 to-orange-500 text-white hover:from-rose-400 hover:to-orange-400 shadow-lg shadow-rose-500/30">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Issue Code
                  </Button>
                )}
              </div>
            </div>
            {emergencyCode.code && (
              <div className="mt-4 p-4 rounded-xl bg-black/40 border border-rose-500/25">
                <p className="text-[10px] tracking-[0.25em] uppercase text-rose-300/70 mb-2">Active code — copy now, it won't be shown again</p>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xl tracking-widest text-white bg-rose-500/[0.06] px-4 py-2 rounded-lg border border-rose-500/30" data-testid="emergency-code-value">
                    {emergencyCode.code}
                  </span>
                  <Button size="sm" onClick={() => { navigator.clipboard.writeText(emergencyCode.code); toast.success('Copied'); }}
                    className="bg-slate-800 border border-slate-700 text-slate-300 hover:text-white">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <p className="text-[11px] text-rose-300/80 mt-2">Expires {emergencyCode.expires_at ? new Date(emergencyCode.expires_at).toLocaleString() : '—'} · single-use</p>
              </div>
            )}
            {emergencyCode.active && !emergencyCode.code && (
              <div className="mt-4 p-3 rounded-xl bg-amber-500/[0.06] border border-amber-500/25 text-amber-200 text-xs">
                An emergency code is currently active (issued by <span className="font-mono">{emergencyCode.issued_by}</span>) — value hidden. Revoke or wait for expiry.
              </div>
            )}
          </div>

          {/* Live Threat Map — pins every recently active IP */}
          <ThreatMap ips={ipIntel} />

          {/* Approvals & IP Locks — new gate management */}
          <ApprovalsPanel getAuthHeaders={getAuthHeaders} stepUpHeaders={stepUpHeaders} withStepUp={withStepUp} />

          {/* IP Intelligence */}
          <div className="rounded-2xl border border-slate-800/60 bg-slate-900/40 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-cyan-300/70">Traffic Sources</p>
                <h3 className="text-white font-semibold text-base">IP Intelligence · Last {forensicFilter.hours}h</h3>
                <p className="text-slate-500 text-xs mt-0.5">Every real IP that touched the portal — grouped by geo &amp; action count</p>
              </div>
              <div className="flex gap-1.5">
                {[1, 24, 168].map(h => (
                  <button key={h} onClick={() => setForensicFilter(f => ({ ...f, hours: h }))} data-testid={`forensic-hours-${h}`}
                    className={`px-2.5 py-1 text-[11px] rounded-lg border transition ${forensicFilter.hours === h ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-200' : 'border-slate-700 text-slate-400 hover:text-white'}`}>
                    {h === 1 ? '1h' : h === 24 ? '24h' : '7d'}
                  </button>
                ))}
                <Button onClick={fetchForensics} size="sm" variant="ghost" data-testid="refresh-forensics"
                  className="h-7 px-2 text-slate-400 hover:text-white">
                  <RefreshCw className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            {ipIntel.length === 0 ? (
              <p className="text-slate-500 text-xs text-center py-6">No IPs recorded yet — activity will appear here in real time.</p>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {ipIntel.map((row, i) => (
                  <div key={i} data-testid={`ip-intel-row-${i}`}
                    className={`p-3 rounded-xl border ${row.is_blocked ? 'border-rose-500/40 bg-rose-500/[0.04]' : row.suspicious ? 'border-amber-500/30 bg-amber-500/[0.03]' : 'border-slate-800/60 bg-slate-800/20'} flex items-center gap-3`}>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${row.is_blocked ? 'bg-rose-400' : row.suspicious ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-mono text-sm text-white">{row.ip}</span>
                        {row.geo?.country && (
                          <span className="text-xs text-slate-400">
                            {row.geo.city ? `${row.geo.city}, ` : ''}{row.geo.country}
                            {row.geo.country_code && ` (${row.geo.country_code})`}
                          </span>
                        )}
                        {row.geo?.is_hosting && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">Hosting</span>}
                        {row.geo?.is_proxy && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300">Proxy/VPN</span>}
                        {row.is_blocked && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/25 text-rose-200">Blocked</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500 flex-wrap">
                        <span>{row.count} events</span>
                        {row.failed_events > 0 && <span className="text-amber-300">{row.failed_events} suspicious</span>}
                        {row.geo?.isp && <span className="truncate max-w-[220px]">{row.geo.isp}</span>}
                        {row.unique_users.length > 0 && (
                          <span className="text-cyan-300">as: {row.unique_users.slice(0, 3).join(', ')}{row.unique_users.length > 3 ? `+${row.unique_users.length - 3}` : ''}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" onClick={() => setForensicFilter(f => ({ ...f, ip: row.ip }))} data-testid={`filter-by-ip-${i}`}
                        className="h-7 px-2 text-[10px] bg-slate-800 border border-slate-700 text-slate-300 hover:text-white">
                        Trace
                      </Button>
                      {!row.is_blocked ? (
                        <Button size="sm" onClick={() => blockIPManually(row.ip, 'Manual block from IP Intel')} data-testid={`block-ip-${i}`}
                          className="h-7 px-2 text-[10px] bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20">
                          Block
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => unblockIP(row.ip)} data-testid={`unblock-ip-${i}`}
                          className="h-7 px-2 text-[10px] bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20">
                          Unblock
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Forensic Event Stream */}
          <div className="rounded-2xl border border-slate-800/60 bg-slate-900/40 p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-violet-300/70">Deep Forensics</p>
                <h3 className="text-white font-semibold text-base">Live Audit Stream — Who Did What</h3>
                <p className="text-slate-500 text-xs mt-0.5">Every mutation on the portal with real IP, geo, actor and status</p>
              </div>
              <div className="flex gap-1.5">
                {['', 'info', 'warning', 'critical'].map(s => (
                  <button key={s || 'all'} onClick={() => setForensicFilter(f => ({ ...f, severity: s }))} data-testid={`forensic-sev-${s || 'all'}`}
                    className={`px-2.5 py-1 text-[11px] rounded-lg border transition capitalize ${forensicFilter.severity === s ? 'bg-violet-500/15 border-violet-500/40 text-violet-200' : 'border-slate-700 text-slate-400 hover:text-white'}`}>
                    {s || 'All'}
                  </button>
                ))}
              </div>
            </div>
            {forensicFilter.ip && (
              <div className="mb-3 flex items-center gap-2 text-xs">
                <span className="text-slate-400">Filtering by IP:</span>
                <span className="font-mono text-white bg-cyan-500/10 border border-cyan-500/30 px-2 py-0.5 rounded">{forensicFilter.ip}</span>
                <button onClick={() => setForensicFilter(f => ({ ...f, ip: '' }))} className="text-slate-500 hover:text-white">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {forensicEvents.length === 0 ? (
              <p className="text-slate-500 text-xs text-center py-6">No events matching your filters.</p>
            ) : (
              <div className="space-y-1 max-h-[560px] overflow-y-auto font-mono text-[11px]">
                {forensicEvents.map((e, i) => (
                  <div key={i} data-testid={`forensic-event-${i}`}
                    className={`p-2 rounded border-l-2 flex items-start gap-2 hover:bg-white/[0.02] transition ${
                      e.severity === 'critical' ? 'border-rose-500 bg-rose-500/[0.03]' :
                      e.severity === 'warning' ? 'border-amber-500 bg-amber-500/[0.02]' :
                      'border-slate-700'
                    }`}>
                    <span className="text-slate-500 flex-shrink-0 w-32">{new Date(e.timestamp).toLocaleString('en-GB', { hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                    <span className={`flex-shrink-0 w-20 truncate ${e.username === 'anonymous' ? 'text-slate-500' : 'text-cyan-300'}`}>{e.username}</span>
                    <span className="text-slate-300 flex-1 min-w-0 truncate">{e.action}</span>
                    <span className="text-slate-500 flex-shrink-0 truncate max-w-[200px]">
                      {e.ip}
                      {e.geo?.country_code && ` · ${e.geo.country_code}`}
                      {e.status_code && (
                        <span className={`ml-1 ${e.status_code >= 400 ? 'text-rose-300' : 'text-emerald-300'}`}>[{e.status_code}]</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Threats Tab */}
      {activeTab === 'threats' && (
        <div className="space-y-5">
          {/* Stat cards + Emergency lockdown toggle */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-red-500/25 bg-red-500/[0.04] p-4">
              <p className="text-[10px] uppercase tracking-wider text-red-400/80">Blocked IPs</p>
              <p className="text-2xl font-bold text-red-400 tabular-nums mt-1">{threats.blocked?.length || 0}</p>
            </div>
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-4">
              <p className="text-[10px] uppercase tracking-wider text-amber-400/80">Suspicious</p>
              <p className="text-2xl font-bold text-amber-400 tabular-nums mt-1">{threats.suspicious?.length || 0}</p>
            </div>
            <div className="rounded-xl border border-cyan-500/25 bg-cyan-500/[0.04] p-4">
              <p className="text-[10px] uppercase tracking-wider text-cyan-400/80">Step-Up Status</p>
              <p className={`text-sm font-bold mt-1 ${isStepUpValid() ? 'text-emerald-400' : 'text-slate-500'}`}>
                {isStepUpValid() ? `✓ Verified · expires in ${Math.max(0, Math.floor((stepUpExpiresAt - Date.now())/60000))}m` : '⚠ Not verified'}
              </p>
            </div>
            <button onClick={toggleEmergencyLockdown} data-testid="emergency-lockdown-toggle"
              className={`rounded-xl border-2 p-4 text-left transition-all ${
                lockdownState.enabled
                  ? 'bg-red-500/15 border-red-500/50 hover:bg-red-500/25'
                  : 'bg-slate-900/60 border-slate-700/40 hover:border-red-500/40'
              }`}>
              <div className="flex items-center gap-2">
                <Lock className={`w-4 h-4 ${lockdownState.enabled ? 'text-red-400' : 'text-slate-400'}`} />
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Emergency Lockdown</p>
              </div>
              <p className={`text-sm font-bold mt-1 ${lockdownState.enabled ? 'text-red-300' : 'text-slate-300'}`}>
                {lockdownState.enabled ? '🔒 ACTIVE — click to disable' : 'Click to enable'}
              </p>
            </button>
          </div>

          {/* Destructive actions panel */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
            <div className="px-5 py-3 bg-gradient-to-r from-red-500/[0.05] to-slate-900/80 border-b border-slate-800 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-red-400" />
              <h3 className="text-sm font-medium text-white">Destructive Actions <span className="text-[10px] uppercase tracking-wider text-red-400/80 ml-2">step-up required</span></h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 divide-x divide-slate-800">
              <button onClick={rotateSecurityCode} data-testid="rotate-code-btn"
                className="p-5 text-left hover:bg-amber-500/[0.05] transition-colors disabled:opacity-50"
                disabled={lockdownState.enabled}>
                <KeyRound className="w-5 h-5 text-amber-400 mb-2" />
                <p className="text-white text-sm font-semibold">Rotate Security Gate Code</p>
                <p className="text-slate-500 text-[11px] mt-0.5">Set a custom code or auto-generate. Old code is invalid immediately.</p>
              </button>
              <button onClick={revokeAllSessions} data-testid="revoke-sessions-btn"
                className="p-5 text-left hover:bg-orange-500/[0.05] transition-colors disabled:opacity-50"
                disabled={lockdownState.enabled}>
                <LogOut className="w-5 h-5 text-orange-400 mb-2" />
                <p className="text-white text-sm font-semibold">Force-Logout Everyone</p>
                <p className="text-slate-500 text-[11px] mt-0.5">Bumps token_version on all accounts. Every staff member must log in again.</p>
              </button>
              <button onClick={() => withStepUp(async () => {
                if (!window.confirm('Force ALL non-superadmin staff to change their password on next login? Continue?')) return;
                try {
                  const r = await axios.post(`${BACKEND_URL}/api/admin/force-password-reset-all`, {}, { headers: stepUpHeaders() });
                  toast.success(`Forced ${r.data.affected || 0} staff to rotate passwords`);
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }, 'forcing password resets')} data-testid="force-pwd-reset-btn"
                className="p-5 text-left hover:bg-red-500/[0.05] transition-colors disabled:opacity-50"
                disabled={lockdownState.enabled}>
                <RefreshCw className="w-5 h-5 text-red-400 mb-2" />
                <p className="text-white text-sm font-semibold">Force Password Rotation</p>
                <p className="text-slate-500 text-[11px] mt-0.5">All non-superadmin staff must set a new password on next login.</p>
              </button>
            </div>
          </div>

          {/* Blocked IPs list */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
            <div className="px-5 py-3 bg-gradient-to-r from-red-500/[0.05] to-slate-900/80 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-sm font-medium text-white flex items-center gap-2">
                <Globe className="w-4 h-4 text-red-400" />
                Blocked IPs ({threats.blocked?.length || 0})
              </h3>
              <Button onClick={fetchThreats} variant="outline" size="sm" className="h-7 text-xs border-slate-700 text-slate-400 hover:text-white">
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
            </div>
            {!threats.blocked || threats.blocked.length === 0 ? (
              <div className="py-10 text-center text-slate-500 text-xs italic">No IPs currently blocked. Honeypot &amp; scanner detection is actively guarding.</div>
            ) : (
              <div className="divide-y divide-slate-800/40">
                {threats.blocked.map((ip) => (
                  <div key={ip} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-800/30">
                    <div className="w-8 h-8 rounded-lg bg-red-500/15 border border-red-500/25 flex items-center justify-center">
                      <Lock className="w-3.5 h-3.5 text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-mono">{ip}</p>
                      <p className="text-slate-500 text-[10px] mt-0.5">Caught by threat-detection middleware</p>
                    </div>
                    <Button onClick={() => unblockIP(ip)} variant="outline" size="sm" data-testid={`unblock-${ip}`}
                      className="h-7 text-xs border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">
                      Unblock
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Suspicious activity */}
          {threats.suspicious && threats.suspicious.length > 0 && (
            <div className="rounded-xl border border-amber-500/20 bg-slate-900/60 overflow-hidden">
              <div className="px-5 py-3 bg-gradient-to-r from-amber-500/[0.05] to-slate-900/80 border-b border-amber-500/20">
                <h3 className="text-sm font-medium text-white flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  Suspicious Activity ({threats.suspicious.length})
                </h3>
              </div>
              <div className="divide-y divide-slate-800/40">
                {threats.suspicious.map((s) => (
                  <div key={s.ip} className="flex items-center gap-3 px-5 py-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/25 flex items-center justify-center">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-mono">{s.ip}</p>
                      <p className="text-slate-500 text-[10px]">{s.count} suspicious requests</p>
                    </div>
                    <Button onClick={() => blockIPManually(s.ip, 'Manually blocked from suspicious activity')} variant="outline" size="sm"
                      className="h-7 text-xs border-red-500/30 text-red-300 hover:bg-red-500/10">
                      Block now
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Threat Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { label: 'Audit Events', value: stats.totalEvents, icon: Activity, color: 'cyan', sub: 'Total recorded' },
              { label: 'Failed Logins', value: stats.failedLogins, icon: XCircle, color: 'red', sub: 'Last 24 hours' },
              { label: 'Successful Logins', value: stats.successLogins, icon: CheckCircle, color: 'emerald', sub: 'Last 24 hours' },
              { label: 'Code Attempts', value: stats.codeAttempts, icon: ShieldAlert, color: 'amber', sub: 'Failed (24h)' },
              { label: 'Locked Accounts', value: stats.lockedNow, icon: Lock, color: stats.lockedNow > 0 ? 'red' : 'slate', sub: 'Currently locked' },
            ].map((stat, i) => (
              <div key={i} data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="p-4 rounded-xl bg-slate-900/60 border border-slate-800/60 hover:border-slate-700/60 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-9 h-9 rounded-lg bg-${stat.color}-500/10 border border-${stat.color}-500/20 flex items-center justify-center`}>
                    <stat.icon className={`w-4.5 h-4.5 text-${stat.color}-400`} />
                  </div>
                  <span className={`text-2xl font-bold ${stat.value > 0 && stat.color === 'red' ? 'text-red-400' : 'text-white'} tracking-tight`}>{stat.value}</span>
                </div>
                <p className="text-sm font-medium text-slate-300">{stat.label}</p>
                <p className="text-xs text-slate-600 mt-0.5">{stat.sub}</p>
              </div>
            ))}
          </div>

          {/* Security Score & Threat Summary */}
          {threatSummary && isSuperAdmin && (
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Security Score */}
              <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800/60" data-testid="security-score">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold flex items-center gap-2"><Shield className="w-5 h-5 text-cyan-400" /> Security Score</h3>
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold ${
                    threatSummary.security_score >= 80 ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                    threatSummary.security_score >= 50 ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                    'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>{threatSummary.security_score}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-slate-800/30">
                    <p className="text-red-400 text-xl font-bold">{threatSummary.failed_logins_today}</p>
                    <p className="text-slate-500 text-[10px]">Failed logins today</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/30">
                    <p className="text-amber-400 text-xl font-bold">{threatSummary.warnings_today}</p>
                    <p className="text-slate-500 text-[10px]">Warnings today</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/30">
                    <p className="text-red-400 text-xl font-bold">{threatSummary.blocked_ips}</p>
                    <p className="text-slate-500 text-[10px]">Blocked IPs</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/30">
                    <p className="text-slate-300 text-xl font-bold">{threatSummary.locked_accounts}</p>
                    <p className="text-slate-500 text-[10px]">Locked accounts</p>
                  </div>
                </div>
              </div>

              {/* Recent Audit Log */}
              <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800/60" data-testid="audit-log-preview">
                <h3 className="text-white font-semibold flex items-center gap-2 mb-3"><Activity className="w-5 h-5 text-cyan-400" /> Recent Security Events</h3>
                <div className="space-y-1.5 max-h-52 overflow-y-auto">
                  {auditLogs.length === 0 ? (
                    <p className="text-slate-600 text-xs text-center py-4">No security events recorded</p>
                  ) : auditLogs.slice(0, 10).map((log, i) => (
                    <div key={i} className="flex items-center gap-2.5 p-2 rounded-lg bg-slate-800/30">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        log.severity === 'critical' ? 'bg-red-500' : log.severity === 'warning' ? 'bg-amber-500' : 'bg-emerald-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-[11px] truncate">{log.action}</p>
                        <p className="text-slate-600 text-[9px]">{log.username} · {log.ip} · {log.timestamp ? new Date(log.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Emergency Actions */}
          {isSuperAdmin && (
            <div className="p-5 rounded-xl bg-red-500/[0.04] border border-red-500/20">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <h3 className="text-lg font-semibold text-red-400">Emergency Actions</h3>
                <Badge className="bg-red-500/10 text-red-400 border border-red-500/20 text-[10px]">SUPERADMIN</Badge>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Use these actions when a security breach is suspected. These cannot be undone.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button onClick={() => setShowChangeCode(true)} data-testid="change-code-btn"
                  className="p-4 rounded-xl bg-slate-900/60 border border-slate-700/50 hover:border-amber-500/30 transition-all text-left group">
                  <KeyRound className="w-5 h-5 text-amber-400 mb-2" />
                  <p className="text-sm font-medium text-white">Rotate Security Code</p>
                  <p className="text-xs text-slate-500 mt-1">Change the portal access code immediately</p>
                </button>
                <button onClick={() => setShowForceReset(true)} data-testid="force-reset-btn"
                  className="p-4 rounded-xl bg-slate-900/60 border border-slate-700/50 hover:border-red-500/30 transition-all text-left group">
                  <RotateCcw className="w-5 h-5 text-red-400 mb-2" />
                  <p className="text-sm font-medium text-white">Force Password Reset</p>
                  <p className="text-xs text-slate-500 mt-1">All staff must change passwords on next login</p>
                </button>
                <button onClick={() => setShowInvalidateSessions(true)} data-testid="invalidate-sessions-btn"
                  className="p-4 rounded-xl bg-slate-900/60 border border-slate-700/50 hover:border-red-500/30 transition-all text-left group">
                  <LogOut className="w-5 h-5 text-red-400 mb-2" />
                  <p className="text-sm font-medium text-white">Invalidate All Sessions</p>
                  <p className="text-xs text-slate-500 mt-1">Force re-login for all staff members</p>
                </button>
              </div>
            </div>
          )}

          {/* Current Security Code */}
          {isSuperAdmin && currentCode && (
            <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800/60">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-amber-400" />
                  <h3 className="text-base font-semibold text-white">Current Security Code</h3>
                </div>
                <button onClick={() => setShowCode(!showCode)} data-testid="toggle-code-visibility"
                  className="text-slate-500 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-slate-800">
                  {showCode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  {(showCode ? currentCode.code : '********').split('').map((c, i) => (
                    <div key={i} className="w-9 h-11 bg-black/40 border border-slate-700 rounded-lg flex items-center justify-center">
                      <span className="text-white font-mono text-lg">{c}</span>
                    </div>
                  ))}
                </div>
                {showCode && (
                  <button onClick={() => { navigator.clipboard.writeText(currentCode.code); toast.success('Code copied'); }}
                    className="p-2 rounded-lg hover:bg-slate-800 text-slate-500 hover:text-white transition-colors">
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs text-slate-600">
                <span>Last changed by: <span className="text-slate-400">{currentCode.updated_by || 'system'}</span></span>
                {currentCode.updated_at && <span>on {formatTimestamp(currentCode.updated_at)}</span>}
              </div>
            </div>
          )}

          {/* Locked Accounts */}
          {lockedAccounts.length > 0 && (
            <div className="p-5 rounded-xl bg-amber-500/[0.04] border border-amber-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-5 h-5 text-amber-400" />
                <h3 className="text-base font-semibold text-amber-400">Locked Accounts ({lockedAccounts.length})</h3>
              </div>
              <div className="space-y-2">
                {lockedAccounts.map((acc, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/60 border border-slate-800/60">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <span className="text-amber-400 text-xs font-bold">{acc.username?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{acc.username}</p>
                        <p className="text-xs text-slate-500">{acc.attempts} failed attempts</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-amber-400">{Math.ceil(acc.remaining_seconds / 60)}m remaining</span>
                      {isSuperAdmin && (
                        <Button onClick={() => handleUnlockAccount(acc.username)} size="sm" data-testid={`unlock-${acc.username}`}
                          className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/20">
                          <Unlock className="w-3.5 h-3.5 mr-1" /> Unlock
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Successful Logins — Forensic panel for incident response */}
          <div className="rounded-xl border border-emerald-500/15 overflow-hidden" data-testid="recent-logins-panel">
            <div className="px-5 py-3 bg-gradient-to-r from-emerald-500/[0.06] to-slate-900/80 border-b border-emerald-500/20 flex items-center justify-between">
              <h3 className="text-sm font-medium text-white flex items-center gap-2">
                <Fingerprint className="w-4 h-4 text-emerald-400" />
                Recent Successful Logins
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">forensics</span>
              </h3>
              <button onClick={() => setSearchAudit('login_success') || setActiveTab('audit')} className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                Filter audit log →
              </button>
            </div>
            <div className="divide-y divide-slate-800/40">
              {(() => {
                const logins = auditLog.filter(e => e.event_type === 'login_success').slice(0, 8);
                if (logins.length === 0) {
                  return <div className="py-8 text-center text-slate-500 text-xs italic">No successful logins recorded yet. New logins will appear here automatically.</div>;
                }
                return logins.map((event, i) => {
                  const detailsObj = (event.details && typeof event.details === 'object') ? event.details : {};
                  const role = detailsObj.role || '';
                  const ua = detailsObj.user_agent || '';
                  const uaShort = ua.length > 50 ? ua.slice(0, 50) + '…' : ua;
                  return (
                    <div key={i} className="flex items-center gap-3 px-5 py-3 hover:bg-emerald-500/[0.03] transition-colors" data-testid={`login-row-${i}`}>
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center flex-shrink-0">
                        <span className="text-[11px] font-bold text-emerald-300">{(event.username || '?').charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-sm font-medium">@{event.username}</span>
                          {role && (
                            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ${
                              role === 'superadmin' ? 'bg-amber-500/15 text-amber-300 border border-amber-500/25' :
                              role === 'executive' ? 'bg-purple-500/15 text-purple-300 border border-purple-500/25' :
                              role === 'admin' ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/25' :
                              'bg-slate-500/15 text-slate-300 border border-slate-500/25'
                            }`}>{role}</span>
                          )}
                        </div>
                        {uaShort && <p className="text-[10px] text-slate-500 truncate mt-0.5" title={ua}>{uaShort}</p>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-[10px] text-cyan-400 font-mono">{event.ip || '—'}</span>
                        <p className="text-[10px] text-slate-600 whitespace-nowrap">{getRelativeTime(event.timestamp)}</p>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* Recent Activity Preview */}
          <div className="rounded-xl border border-slate-800/60 overflow-hidden">
            <div className="px-5 py-3 bg-slate-900/80 border-b border-slate-800/60 flex items-center justify-between">
              <h3 className="text-sm font-medium text-white">Recent Security Events</h3>
              <button onClick={() => setActiveTab('audit')} className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors">
                View All
              </button>
            </div>
            <div className="divide-y divide-slate-800/40">
              {auditLog.slice(0, 8).map((event, i) => (
                <div key={i} className="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-800/20 transition-colors">
                  {getEventIcon(event.event_type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-300">
                      <span className="font-medium text-white">{event.details?.username || (event.event_type || 'unknown').replace(/_/g, ' ')}</span>
                      {event.details?.username && <span className="text-slate-500"> - {(event.event_type || 'unknown').replace(/_/g, ' ')}</span>}
                    </p>
                  </div>
                  <span className="text-xs text-slate-600 flex-shrink-0">{getRelativeTime(event.timestamp)}</span>
                  {event.ip && event.ip !== 'admin' && (
                    <span className="text-[10px] text-slate-700 font-mono">{event.ip}</span>
                  )}
                </div>
              ))}
              {auditLog.length === 0 && (
                <div className="py-10 text-center text-slate-500 text-sm">No security events recorded yet</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input type="text" value={searchAudit} onChange={(e) => setSearchAudit(e.target.value)}
                placeholder="Search by username, IP, event type..." data-testid="search-audit"
                className="w-full pl-10 pr-4 py-2.5 bg-slate-900/60 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:border-cyan-500/40 focus:outline-none text-sm" />
            </div>
            <select value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)} data-testid="filter-events"
              className="px-3 py-2.5 bg-slate-900/60 border border-slate-800 rounded-xl text-sm text-slate-300 focus:border-cyan-500/40 focus:outline-none">
              <option value="all">All Events</option>
              {eventTypes.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
            <Button onClick={() => {
              try {
                const blob = new Blob([JSON.stringify({
                  exported_at: new Date().toISOString(),
                  total: filteredAudit.length,
                  events: filteredAudit,
                }, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `audit-log-${new Date().toISOString().slice(0,10)}.json`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success(`Exported ${filteredAudit.length} events`);
              } catch {
                toast.error('Export failed');
              }
            }} data-testid="export-audit-btn"
              className="bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 h-auto py-2.5 px-4 text-sm gap-2 whitespace-nowrap">
              <Download className="w-3.5 h-3.5" /> Export JSON
            </Button>
          </div>

          {/* Severity Filter Chips */}
          <div className="flex flex-wrap items-center gap-1.5 p-1 rounded-lg bg-slate-900/40 border border-slate-800/40 inline-flex">
            {[
              { id: 'all', label: 'All', dotColor: 'bg-slate-400', count: auditLog.length },
              { id: 'critical', label: 'Critical', dotColor: 'bg-red-500', count: auditLog.filter(e => e.severity === 'critical').length },
              { id: 'warning', label: 'Warnings', dotColor: 'bg-amber-500', count: auditLog.filter(e => e.severity === 'warning').length },
              { id: 'info', label: 'Info', dotColor: 'bg-emerald-500', count: auditLog.filter(e => e.severity === 'info' || !e.severity).length },
            ].map(s => {
              const active = (severityFilter || 'all') === s.id;
              return (
                <button key={s.id} onClick={() => setSeverityFilter(s.id)}
                  data-testid={`severity-filter-${s.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    active
                      ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white shadow-lg shadow-cyan-500/20'
                      : 'text-slate-400 hover:text-white'
                  }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${s.dotColor}`} />
                  {s.label}
                  <span className={`text-[10px] tabular-nums ${active ? 'text-white/70' : 'text-slate-600'}`}>{s.count}</span>
                </button>
              );
            })}
          </div>

          {/* Audit Table */}
          <div className="rounded-xl border border-slate-800/60 overflow-hidden" data-testid="audit-log-table">
            <div className="grid grid-cols-[auto_1fr_1fr_1fr_auto] gap-4 px-5 py-3 bg-slate-900/80 border-b border-slate-800/60 text-xs font-medium text-slate-500 uppercase tracking-wider">
              <div className="w-8"></div>
              <div>Event</div>
              <div>Details</div>
              <div>IP Address</div>
              <div>Time</div>
            </div>
            <div className="divide-y divide-slate-800/40 max-h-[600px] overflow-y-auto">
              {filteredAudit.length === 0 ? (
                <div className="py-16 text-center text-slate-500 text-sm">No events found</div>
              ) : filteredAudit.map((event, i) => {
                const details = event.details;
                const detailsObj = (details && typeof details === 'object') ? details : null;
                const detailsStr = (typeof details === 'string') ? details : '';
                return (
                  <div key={i} className="grid grid-cols-[auto_1fr_1fr_1fr_auto] gap-4 px-5 py-3 items-center hover:bg-slate-800/20 transition-colors" data-testid={`audit-row-${i}`}>
                    <div className="w-8 flex items-center justify-center">{getEventIcon(event.event_type)}</div>
                    <div>
                      <Badge className={`${getEventBadgeColor(event.event_type)} text-[10px] border`}>
                        {event.event_type?.replace(/_/g, ' ')}
                      </Badge>
                      {event.severity && event.severity !== 'info' && (
                        <span className={`ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
                          event.severity === 'critical' ? 'bg-red-500/15 text-red-400 border-red-500/30' :
                          event.severity === 'warning' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' :
                          'bg-slate-500/15 text-slate-400 border-slate-500/30'
                        }`}>{event.severity}</span>
                      )}
                    </div>
                    <div className="text-sm text-slate-400 truncate">
                      {event.username && event.username !== 'system' && (
                        <span className="text-white font-medium">@{event.username}</span>
                      )}
                      {detailsStr && (
                        <span className="text-slate-500 ml-1.5">{detailsStr}</span>
                      )}
                      {detailsObj?.username && <span className="text-white font-medium">{detailsObj.username}</span>}
                      {detailsObj?.changed_by && <span>Changed by <span className="text-white">{detailsObj.changed_by}</span></span>}
                      {detailsObj?.triggered_by && <span>By <span className="text-white">{detailsObj.triggered_by}</span></span>}
                      {detailsObj?.affected_count !== undefined && <span className="text-slate-500"> ({detailsObj.affected_count} affected)</span>}
                      {detailsObj?.attempts && <span className="text-slate-500"> (attempt #{detailsObj.attempts})</span>}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">{event.ip && event.ip !== 'admin' ? event.ip : '—'}</div>
                    <div className="text-xs text-slate-600 whitespace-nowrap">{getRelativeTime(event.timestamp)}</div>
                  </div>
                );
              })}
            </div>
            {filteredAudit.length > 0 && (
              <div className="px-5 py-3 bg-slate-900/40 border-t border-slate-800/40">
                <p className="text-xs text-slate-600">Showing {filteredAudit.length} events</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Access Control Tab */}
      {activeTab === 'access' && (
        <div className="space-y-6">
          {/* Security Code Management */}
          <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800/60">
            <div className="flex items-center gap-2 mb-4">
              <KeyRound className="w-5 h-5 text-amber-400" />
              <h3 className="text-base font-semibold text-white">Portal Security Code</h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              The security code is the first gate to your staff portal. Change it immediately if compromised.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/30">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Current Protection</p>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm text-emerald-400 font-medium">Server-side validation</span>
                </div>
                <p className="text-xs text-slate-600 mt-1">3 attempts before 10-minute lockout</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/30">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Login Protection</p>
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-400" />
                  <span className="text-sm text-amber-400 font-medium">Brute force protection</span>
                </div>
                <p className="text-xs text-slate-600 mt-1">5 attempts before 5-minute lockout</p>
              </div>
            </div>
            {isExecutive && (
              <Button onClick={() => setShowChangeCode(true)} className="mt-4 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/20" size="sm" data-testid="change-code-access-btn">
                <KeyRound className="w-4 h-4 mr-1.5" /> Change Security Code
              </Button>
            )}
          </div>

          {/* Locked Accounts Management */}
          <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800/60">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-red-400" />
              <h3 className="text-base font-semibold text-white">Locked Accounts</h3>
            </div>
            {lockedAccounts.length === 0 ? (
              <div className="text-center py-8">
                <Unlock className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No accounts are currently locked</p>
              </div>
            ) : (
              <div className="space-y-2">
                {lockedAccounts.map((acc, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/40 border border-slate-700/30">
                    <div>
                      <p className="text-sm font-medium text-white">{acc.username}</p>
                      <p className="text-xs text-slate-500">{acc.attempts} failed attempts - unlocks in {Math.ceil(acc.remaining_seconds / 60)}m</p>
                    </div>
                    {isSuperAdmin && (
                      <Button onClick={() => handleUnlockAccount(acc.username)} size="sm"
                        className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/20">
                        <Unlock className="w-3.5 h-3.5 mr-1" /> Unlock
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Force Actions */}
          {isSuperAdmin && (
            <div className="p-5 rounded-xl bg-red-500/[0.04] border border-red-500/20">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <h3 className="text-base font-semibold text-red-400">Breach Response Actions</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button onClick={() => setShowForceReset(true)}
                  className="p-4 rounded-xl bg-slate-900/60 border border-slate-700/50 hover:border-red-500/30 transition-all text-left">
                  <RotateCcw className="w-5 h-5 text-red-400 mb-2" />
                  <p className="text-sm font-medium text-white">Force All Password Resets</p>
                  <p className="text-xs text-slate-500 mt-1">Non-superadmin staff must change password on next login</p>
                </button>
                <button onClick={() => setShowInvalidateSessions(true)}
                  className="p-4 rounded-xl bg-slate-900/60 border border-slate-700/50 hover:border-red-500/30 transition-all text-left">
                  <LogOut className="w-5 h-5 text-red-400 mb-2" />
                  <p className="text-sm font-medium text-white">Kill All Active Sessions</p>
                  <p className="text-xs text-slate-500 mt-1">Everyone gets logged out immediately</p>
                </button>
              </div>
            </div>
          )}

          {/* Security Page Authorization - Superadmin Only */}
          {isSuperAdmin && (
            <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800/60" data-testid="security-authorization">
              <div className="flex items-center gap-2 mb-1">
                <UserCheck className="w-5 h-5 text-cyan-400" />
                <h3 className="text-base font-semibold text-white">Security Page Authorization</h3>
              </div>
              <p className="text-sm text-slate-500 mb-5">Grant or revoke access for staff members to view this Security Centre. Superadmins always have access.</p>

              {/* Grant Access */}
              <div className="flex gap-3 mb-5">
                <select value={selectedGrantUser} onChange={(e) => setSelectedGrantUser(e.target.value)}
                  data-testid="grant-access-select"
                  className="flex-1 bg-slate-800/60 border border-slate-700/50 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500/30">
                  <option value="">Select a staff member...</option>
                  {allStaff
                    .filter(s => !authorizedUsers.some(a => a.username === s.username))
                    .map(s => (
                      <option key={s.username} value={s.username}>{s.display_name || s.username} ({s.role})</option>
                    ))
                  }
                </select>
                <Button onClick={() => handleGrantSecurityAccess(selectedGrantUser)}
                  disabled={!selectedGrantUser}
                  className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/20 disabled:opacity-30"
                  data-testid="grant-access-btn">
                  <UserPlus className="w-4 h-4 mr-1.5" /> Grant
                </Button>
              </div>

              {/* Authorized Users List */}
              <div className="space-y-2">
                <p className="text-[10px] text-slate-500 uppercase tracking-[0.15em] font-medium">Authorized Users ({authorizedUsers.length})</p>
                {authorizedUsers.length === 0 ? (
                  <div className="text-center py-6 text-slate-600">
                    <Shield className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No additional users authorized</p>
                    <p className="text-xs mt-1">Only superadmins can access this page</p>
                  </div>
                ) : (
                  authorizedUsers.map(u => (
                    <div key={u.username} className="flex items-center justify-between p-3 rounded-xl bg-slate-800/40 border border-slate-700/30 group" data-testid={`authorized-user-${u.username}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/20">
                          <span className="text-sm font-bold text-cyan-400">{(u.display_name || u.username)?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{u.display_name || u.username}</p>
                          <p className="text-[10px] text-slate-500">@{u.username} · {u.role} · Granted {u.authorized_at ? new Date(u.authorized_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}</p>
                        </div>
                      </div>
                      <Button onClick={() => handleRevokeSecurityAccess(u.username)} size="sm"
                        className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`revoke-${u.username}`}>
                        <UserMinus className="w-3.5 h-3.5 mr-1" /> Revoke
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ LOCKDOWN TAB ═══ */}
      {activeTab === 'lockdown' && (
        <div className="space-y-6">
          {/* Lockdown Status */}
          <div className={`p-6 rounded-xl border ${lockdownEnabled ? 'bg-red-500/[0.06] border-red-500/30' : 'bg-emerald-500/[0.03] border-emerald-500/20'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {lockdownEnabled ? <Lock className="w-6 h-6 text-red-400" /> : <Shield className="w-6 h-6 text-emerald-400" />}
                <div>
                  <h3 className="text-lg font-semibold text-white">{lockdownEnabled ? 'Portal is LOCKED DOWN' : 'Portal is Open'}</h3>
                  <p className="text-xs text-slate-400">{lockdownEnabled ? `Reason: ${lockdownReason || 'No reason specified'}` : 'All staff can access the portal normally'}</p>
                </div>
              </div>
              <Badge className={lockdownEnabled ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'}>
                {lockdownEnabled ? 'ACTIVE' : 'INACTIVE'}
              </Badge>
            </div>
            {isSuperAdmin && (
              <div className="pt-4 border-t border-white/[0.06]">
                {lockdownEnabled ? (
                  <Button onClick={async () => {
                    try { await axios.post(`${BACKEND_URL}/api/admin/security/lockdown`, { enabled: false }, { headers: getAuthHeaders() }); setLockdownEnabled(false); toast.success('Lockdown deactivated'); fetchData(); } catch { toast.error('Failed'); }
                  }} data-testid="deactivate-lockdown-btn"
                    className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30">
                    <Unlock className="w-4 h-4 mr-2" />Deactivate Lockdown
                  </Button>
                ) : (
                  <Button onClick={() => setShowLockdownConfirm(true)} data-testid="activate-lockdown-btn"
                    className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30">
                    <Lock className="w-4 h-4 mr-2" />Activate Lockdown
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Lockdown Info */}
          <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800/60">
            <h3 className="text-base font-semibold text-white mb-3">What happens during lockdown?</h3>
            <div className="space-y-2">
              {[
                { icon: Lock, text: 'Only superadmins can access the portal' },
                { icon: Users, text: 'All other staff are shown a maintenance message' },
                { icon: Activity, text: 'All activities are logged for audit trail' },
                { icon: ShieldCheck, text: 'Existing sessions for non-admins are suspended' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-slate-400">
                  <item.icon className="w-4 h-4 text-slate-600 flex-shrink-0" />
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ SESSIONS TAB ═══ */}
      {activeTab === 'sessions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-white">Active Sessions ({activeSessions.length})</h3>
          </div>
          {activeSessions.length === 0 ? (
            <div className="text-center py-12 rounded-xl bg-slate-900/60 border border-slate-800/60">
              <Globe className="w-12 h-12 mx-auto mb-3 text-slate-700" />
              <p className="text-slate-500 text-sm">No active sessions tracked yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeSessions.map((session, i) => (
                <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-slate-900/60 border border-slate-800/60 hover:border-slate-700/60 transition-all"
                  data-testid={`session-${session.session_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-white">{session.username}</p>
                        {session.is_current && <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[9px]">CURRENT</Badge>}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                        <span>{session.device || 'Unknown'}</span>
                        <span>IP: {session.ip_address}</span>
                        <span>{getRelativeTime(session.last_active || session.started_at)}</span>
                      </div>
                    </div>
                  </div>
                  {(isSuperAdmin || session.username === currentUser?.username) && !session.is_current && (
                    <Button onClick={async () => {
                      try { await axios.delete(`${BACKEND_URL}/api/admin/security/sessions/${session.session_id}`, { headers: getAuthHeaders() }); toast.success('Session revoked'); fetchData(); } catch { toast.error('Failed'); }
                    }} size="sm" data-testid={`revoke-session-${session.session_id}`}
                      className="bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20">
                      <X className="w-3.5 h-3.5 mr-1" />Revoke
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ SITE MANAGEMENT TAB ═══ */}
      {activeTab === 'site' && (
        <div className="space-y-4" data-testid="site-management-tab">
          {/* Sub-tabs */}
          <div className="flex gap-1 bg-slate-900/60 border border-slate-800/60 rounded-xl p-1">
            {[
              { id: 'maintenance', label: 'Maintenance Mode', icon: Wrench },
              { id: 'banners', label: 'Alert Banners', icon: Bell },
              { id: 'schedule', label: 'Schedule', icon: Calendar },
              { id: 'incidents', label: 'Incidents', icon: Radio },
            ].map(t => (
              <button key={t.id} onClick={() => setSiteSubTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
                  siteSubTab === t.id ? 'bg-slate-800 text-white' : 'text-slate-500 hover:text-slate-300'
                }`} data-testid={`site-subtab-${t.id}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {/* Maintenance Mode */}
          {siteSubTab === 'maintenance' && (
            <div className="space-y-4">
              <div className={`p-5 rounded-xl border ${siteMode.enabled ? 'bg-red-500/[0.05] border-red-500/20' : 'bg-slate-900/60 border-slate-800/60'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Wrench className={`w-6 h-6 ${siteMode.enabled ? 'text-red-400' : 'text-slate-500'}`} />
                    <div>
                      <h3 className="text-white font-semibold text-sm">Maintenance Mode</h3>
                      <p className="text-slate-500 text-xs">{siteMode.enabled ? 'Active — visitors are blocked' : 'Inactive — site is accessible'}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-bold ${siteMode.enabled ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'}`}>
                    {siteMode.enabled ? 'ON' : 'OFF'}
                  </span>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">Title</label>
                    <input type="text" value={modeForm.title} onChange={(e) => setModeForm(p => ({ ...p, title: e.target.value }))}
                      className="w-full p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500/40" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">Message</label>
                    <textarea value={modeForm.message} onChange={(e) => setModeForm(p => ({ ...p, message: e.target.value }))}
                      className="w-full p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500/40 resize-none" rows={2} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">Estimated End</label>
                      <input type="datetime-local" value={modeForm.scheduled_end} onChange={(e) => setModeForm(p => ({ ...p, scheduled_end: e.target.value }))}
                        className="w-full p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500/40 [color-scheme:dark]" />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 p-2.5 cursor-pointer">
                        <input type="checkbox" checked={modeForm.allow_browse} onChange={(e) => setModeForm(p => ({ ...p, allow_browse: e.target.checked }))}
                          className="rounded border-slate-600" />
                        <span className="text-sm text-slate-300">Allow browsing (banner only)</span>
                      </label>
                    </div>
                  </div>
                  {/* Scope picker — apply maintenance to which surface */}
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Apply To</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        { id: 'all', label: 'Entire Site', desc: 'Public + Staff', icon: Globe, color: 'cyan' },
                        { id: 'public', label: 'Public Site', desc: 'Customers only', icon: Users, color: 'amber' },
                        { id: 'staff', label: 'Staff Portal', desc: 'Internal team', icon: Shield, color: 'purple' },
                        { id: 'account-settings', label: 'Account Settings', desc: 'Profile & 2FA area', icon: User, color: 'violet' },
                        { id: 'portfolio', label: 'Portfolio', desc: 'Our groups grid', icon: Layers, color: 'emerald' },
                        { id: 'dmca', label: 'DMCA Hub', desc: 'Copyright intake', icon: FileText, color: 'rose' },
                      ].map(opt => {
                        const active = modeForm.scope === opt.id;
                        return (
                          <button key={opt.id} onClick={() => setModeForm(p => ({ ...p, scope: opt.id }))}
                            data-testid={`maintenance-scope-${opt.id}`}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              active
                                ? `bg-${opt.color}-500/15 border-${opt.color}-500/40 shadow-[0_0_12px_rgba(34,211,238,0.10)]`
                                : 'bg-slate-800/30 border-slate-700/40 hover:border-slate-600/60'
                            }`}>
                            <div className="flex items-center gap-2 mb-0.5">
                              <opt.icon className={`w-3.5 h-3.5 ${active ? `text-${opt.color}-400` : 'text-slate-500'}`} />
                              <span className={`text-xs font-semibold ${active ? 'text-white' : 'text-slate-300'}`}>{opt.label}</span>
                            </div>
                            <p className="text-[10px] text-slate-500">{opt.desc}</p>
                          </button>
                        );
                      })}
                    </div>
                    {modeForm.scope === 'staff' && (
                      <p className="mt-2 text-[11px] text-purple-300 flex items-center gap-1.5">
                        <Shield className="w-3 h-3" />
                        The public site stays online — only staff portal users see maintenance.
                      </p>
                    )}
                    {modeForm.scope === 'public' && (
                      <p className="mt-2 text-[11px] text-amber-300 flex items-center gap-1.5">
                        <AlertTriangle className="w-3 h-3" />
                        Customers see maintenance — staff can still access internal tools.
                      </p>
                    )}
                    {modeForm.scope === 'account-settings' && (
                      <p className="mt-2 text-[11px] text-violet-300 flex items-center gap-1.5">
                        <User className="w-3 h-3" />
                        Only the Account Settings area is locked — staff can still use the rest of the portal.
                      </p>
                    )}
                    {modeForm.scope === 'portfolio' && (
                      <p className="mt-2 text-[11px] text-emerald-300 flex items-center gap-1.5">
                        <Layers className="w-3 h-3" />
                        Public homepage stays live — only the /portfolio grid displays maintenance.
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 pt-2">
                    {siteMode.enabled ? (
                      <Button onClick={() => toggleMaintenanceMode(false)} className="flex-1 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30" data-testid="deactivate-maintenance">
                        <CheckCircle className="w-4 h-4 mr-1.5" /> End Maintenance
                      </Button>
                    ) : (
                      <Button onClick={() => toggleMaintenanceMode(true)} className="flex-1 bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30" data-testid="activate-maintenance">
                        <Wrench className="w-4 h-4 mr-1.5" /> Activate Maintenance
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-slate-900/40 border border-slate-800/40">
                <div className="flex items-center gap-2 text-slate-500 text-xs">
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Public status page available at</span>
                  <a href="/system-status" target="_blank" className="text-cyan-400 hover:underline">/system-status</a>
                </div>
              </div>
            </div>
          )}

          {/* Alert Banners */}
          {siteSubTab === 'banners' && (
            <div className="space-y-4">
              <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800/60">
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-cyan-400" /> Create Alert Banner</h3>
                <div className="space-y-3">
                  <input type="text" value={newAlert.message} onChange={(e) => setNewAlert(p => ({ ...p, message: e.target.value }))}
                    placeholder="Alert message..." data-testid="alert-message-input"
                    className="w-full p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500/40" />
                  <div className="grid grid-cols-3 gap-3">
                    <select value={newAlert.type} onChange={(e) => setNewAlert(p => ({ ...p, type: e.target.value }))}
                      className="p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none [color-scheme:dark]" data-testid="alert-type-select">
                      <option value="info">Info (Blue)</option>
                      <option value="warning">Warning (Amber)</option>
                      <option value="critical">Critical (Red)</option>
                      <option value="success">Success (Green)</option>
                    </select>
                    <input type="text" value={newAlert.link_text} onChange={(e) => setNewAlert(p => ({ ...p, link_text: e.target.value }))}
                      placeholder="Link text (optional)" className="p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none" />
                    <input type="text" value={newAlert.link_url} onChange={(e) => setNewAlert(p => ({ ...p, link_url: e.target.value }))}
                      placeholder="Link URL (optional)" className="p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none" />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={newAlert.dismissible} onChange={(e) => setNewAlert(p => ({ ...p, dismissible: e.target.checked }))} className="rounded border-slate-600" />
                      <span className="text-sm text-slate-400">Dismissible</span>
                    </label>
                    <Button onClick={createAlert} size="sm" className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30" data-testid="create-alert-btn">
                      <Send className="w-3.5 h-3.5 mr-1.5" /> Publish
                    </Button>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {siteAlerts.length === 0 ? (
                  <div className="text-center py-12 rounded-xl bg-slate-900/40 border border-slate-800/40">
                    <Bell className="w-10 h-10 mx-auto mb-2 text-slate-700" />
                    <p className="text-slate-500 text-sm">No alert banners</p>
                  </div>
                ) : siteAlerts.map(a => {
                  const colors = { info: 'border-blue-500/20 text-blue-400', warning: 'border-amber-500/20 text-amber-400', critical: 'border-red-500/20 text-red-400', success: 'border-emerald-500/20 text-emerald-400' };
                  return (
                    <div key={a.id} className={`p-3 rounded-xl bg-slate-900/40 border ${colors[a.type]?.split(' ')[0] || 'border-slate-700'} flex items-center justify-between`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${a.active ? (colors[a.type] || '') : 'text-slate-600'} bg-current/10`}>{a.type}</span>
                        <p className={`text-sm truncate ${a.active ? 'text-slate-300' : 'text-slate-600 line-through'}`}>{a.message}</p>
                      </div>
                      <div className="flex items-center gap-1.5 ml-3">
                        <Button onClick={() => toggleAlertActive(a.id)} size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-white">
                          {a.active ? <ToggleRight className="w-4 h-4 text-emerald-400" /> : <ToggleLeft className="w-4 h-4" />}
                        </Button>
                        <Button onClick={() => deleteAlert(a.id)} size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-red-400">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Schedule */}
          {siteSubTab === 'schedule' && (
            <div className="space-y-4">
              <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800/60">
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-cyan-400" /> Schedule Maintenance</h3>
                <div className="space-y-3">
                  <input type="text" value={newSchedule.title} onChange={(e) => setNewSchedule(p => ({ ...p, title: e.target.value }))}
                    placeholder="Maintenance title..." data-testid="schedule-title-input"
                    className="w-full p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500/40" />
                  <textarea value={newSchedule.description} onChange={(e) => setNewSchedule(p => ({ ...p, description: e.target.value }))}
                    placeholder="Description (optional)..."
                    className="w-full p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none resize-none" rows={2} />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Start</label>
                      <input type="datetime-local" value={newSchedule.scheduled_start} onChange={(e) => setNewSchedule(p => ({ ...p, scheduled_start: e.target.value }))}
                        className="w-full p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none [color-scheme:dark]" data-testid="schedule-start" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">End</label>
                      <input type="datetime-local" value={newSchedule.scheduled_end} onChange={(e) => setNewSchedule(p => ({ ...p, scheduled_end: e.target.value }))}
                        className="w-full p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none [color-scheme:dark]" data-testid="schedule-end" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={newSchedule.auto_activate} onChange={(e) => setNewSchedule(p => ({ ...p, auto_activate: e.target.checked }))} className="rounded border-slate-600" />
                        <span className="text-sm text-slate-400">Auto-activate</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={newSchedule.full_lockdown} onChange={(e) => setNewSchedule(p => ({ ...p, full_lockdown: e.target.checked }))} className="rounded border-slate-600" />
                        <span className="text-sm text-slate-400">Full lockdown</span>
                      </label>
                    </div>
                    <Button onClick={createSchedule} size="sm" className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30" data-testid="schedule-btn">
                      <Calendar className="w-3.5 h-3.5 mr-1.5" /> Schedule
                    </Button>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {scheduledMaint.length === 0 ? (
                  <div className="text-center py-12 rounded-xl bg-slate-900/40 border border-slate-800/40">
                    <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-700" />
                    <p className="text-slate-500 text-sm">No scheduled maintenance</p>
                  </div>
                ) : scheduledMaint.map(s => (
                  <div key={s.id} className="p-3 rounded-xl bg-slate-900/40 border border-slate-800/40 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-white text-sm font-medium">{s.title}</p>
                        {s.activated && <span className="text-[9px] px-1.5 py-px rounded-full bg-emerald-500/15 text-emerald-400 font-semibold">COMPLETED</span>}
                        {s.full_lockdown && !s.activated && <span className="text-[9px] px-1.5 py-px rounded-full bg-red-500/15 text-red-400 font-semibold">LOCKDOWN</span>}
                      </div>
                      <p className="text-slate-500 text-xs flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {new Date(s.scheduled_start).toLocaleString()} - {new Date(s.scheduled_end).toLocaleString()}
                      </p>
                    </div>
                    <Button onClick={() => deleteSchedule(s.id)} size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Incidents */}
          {siteSubTab === 'incidents' && (
            <div className="space-y-4">
              <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800/60">
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Radio className="w-4 h-4 text-red-400" /> Report Incident</h3>
                <div className="space-y-3">
                  <input type="text" value={newIncident.title} onChange={(e) => setNewIncident(p => ({ ...p, title: e.target.value }))}
                    placeholder="Incident title..." data-testid="incident-title-input"
                    className="w-full p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-red-500/30" />
                  <textarea value={newIncident.description} onChange={(e) => setNewIncident(p => ({ ...p, description: e.target.value }))}
                    placeholder="Description..." className="w-full p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm focus:outline-none resize-none" rows={2} />
                  <div className="grid grid-cols-2 gap-3">
                    <select value={newIncident.severity} onChange={(e) => setNewIncident(p => ({ ...p, severity: e.target.value }))}
                      className="p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm [color-scheme:dark]" data-testid="incident-severity">
                      <option value="minor">Minor</option>
                      <option value="major">Major</option>
                      <option value="critical">Critical</option>
                    </select>
                    <select value={newIncident.status} onChange={(e) => setNewIncident(p => ({ ...p, status: e.target.value }))}
                      className="p-2.5 bg-black/20 border border-slate-700 rounded-lg text-white text-sm [color-scheme:dark]">
                      <option value="investigating">Investigating</option>
                      <option value="identified">Identified</option>
                      <option value="monitoring">Monitoring</option>
                    </select>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={createIncident} size="sm" className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30" data-testid="report-incident-btn">
                      <Radio className="w-3.5 h-3.5 mr-1.5" /> Report
                    </Button>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                {incidents.length === 0 ? (
                  <div className="text-center py-12 rounded-xl bg-slate-900/40 border border-slate-800/40">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-500/30" />
                    <p className="text-slate-500 text-sm">No incidents reported</p>
                  </div>
                ) : incidents.map(inc => {
                  const sevColors = { minor: 'text-amber-400', major: 'text-orange-400', critical: 'text-red-400' };
                  const statColors = { investigating: 'text-red-400', identified: 'text-orange-400', monitoring: 'text-amber-400', resolved: 'text-emerald-400' };
                  return (
                    <div key={inc.id} className="p-4 rounded-xl bg-slate-900/40 border border-slate-800/40">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${inc.status === 'resolved' ? 'bg-emerald-500' : 'bg-red-500 animate-pulse'}`} />
                          <p className="text-white font-medium text-sm">{inc.title}</p>
                          <span className={`text-[9px] px-1.5 py-px rounded-full font-bold uppercase ${sevColors[inc.severity] || ''}`}>{inc.severity}</span>
                          <span className={`text-xs ${statColors[inc.status] || ''}`}>{inc.status}</span>
                        </div>
                        <Button onClick={() => deleteIncident(inc.id)} size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-red-400">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {inc.description && <p className="text-slate-400 text-xs mb-2 ml-4">{inc.description}</p>}
                      {inc.updates?.length > 0 && (
                        <div className="ml-4 border-l-2 border-slate-700 pl-3 space-y-1.5 mb-3">
                          {inc.updates.map((u, j) => (
                            <div key={j} className="text-xs">
                              <span className={`${statColors[u.status] || 'text-slate-400'} font-medium`}>{u.status}</span>
                              <span className="text-slate-600 ml-1.5">{new Date(u.updated_at).toLocaleString()}</span>
                              <p className="text-slate-400 mt-0.5">{u.message}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {inc.status !== 'resolved' && (
                        <div className="flex gap-2 mt-2 ml-4">
                          <select value={incidentUpdate.id === inc.id ? incidentUpdate.status : ''} onChange={(e) => setIncidentUpdate({ id: inc.id, status: e.target.value, update_message: incidentUpdate.id === inc.id ? incidentUpdate.update_message : '' })}
                            className="p-1.5 bg-black/20 border border-slate-700 rounded-lg text-white text-xs [color-scheme:dark]">
                            <option value="">Update status...</option>
                            <option value="investigating">Investigating</option>
                            <option value="identified">Identified</option>
                            <option value="monitoring">Monitoring</option>
                            <option value="resolved">Resolved</option>
                          </select>
                          {incidentUpdate.id === inc.id && incidentUpdate.status && (
                            <>
                              <input type="text" value={incidentUpdate.update_message} onChange={(e) => setIncidentUpdate(p => ({ ...p, update_message: e.target.value }))}
                                placeholder="Update message..."
                                className="flex-1 p-1.5 bg-black/20 border border-slate-700 rounded-lg text-white text-xs focus:outline-none" />
                              <Button onClick={updateIncident} size="sm" className="h-7 px-2 text-[10px] bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                                Post
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ ALERTS TAB ═══ */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {securityAlerts.length === 0 ? (
            <div className="text-center py-16 rounded-xl bg-slate-900/60 border border-slate-800/60">
              <ShieldCheck className="w-14 h-14 mx-auto mb-3 text-emerald-500/30" />
              <p className="text-lg font-medium text-emerald-400">All Clear</p>
              <p className="text-slate-500 text-sm mt-1">No security alerts at this time</p>
            </div>
          ) : (
            securityAlerts.map((alert, i) => {
              const severityColors = {
                critical: 'border-red-500/30 bg-red-500/[0.05]',
                high: 'border-red-500/20 bg-red-500/[0.03]',
                medium: 'border-amber-500/20 bg-amber-500/[0.03]',
                low: 'border-slate-700 bg-slate-900/60'
              };
              const iconColors = { critical: 'text-red-400', high: 'text-red-400', medium: 'text-amber-400', low: 'text-slate-400' };
              return (
                <div key={i} className={`p-4 rounded-xl border ${severityColors[alert.severity] || severityColors.low}`} data-testid={`alert-${i}`}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className={`w-5 h-5 mt-0.5 ${iconColors[alert.severity] || 'text-slate-400'}`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-white">{alert.title}</h4>
                        <Badge className={`text-[9px] ${alert.severity === 'critical' ? 'bg-red-500/20 text-red-400 border-red-500/30' : alert.severity === 'high' ? 'bg-red-500/10 text-red-400 border-red-500/20' : alert.severity === 'medium' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-slate-700/50 text-slate-400 border-slate-600'}`}>
                          {alert.severity?.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400">{alert.message}</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Lockdown Confirmation Modal */}
      <Dialog open={showLockdownConfirm} onOpenChange={setShowLockdownConfirm}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm" data-testid="lockdown-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Lock className="w-5 h-5" /> Activate Lockdown
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-xs text-red-400">This will immediately block all non-superadmin staff from accessing the portal.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Reason</label>
              <input type="text" value={lockdownReason} onChange={(e) => setLockdownReason(e.target.value)}
                className="w-full p-3 bg-black/30 border border-slate-700 rounded-lg text-white text-sm focus:border-red-500/30 focus:outline-none"
                placeholder="e.g. Security breach investigation" />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowLockdownConfirm(false)} variant="outline" className="flex-1 border-slate-700 text-slate-400">Cancel</Button>
              <Button onClick={async () => {
                try { await axios.post(`${BACKEND_URL}/api/admin/security/lockdown`, { enabled: true, reason: lockdownReason }, { headers: getAuthHeaders() }); setLockdownEnabled(true); setShowLockdownConfirm(false); toast.success('Portal locked down'); fetchData(); } catch { toast.error('Failed'); }
              }} className="flex-1 bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-lockdown-btn">
                <Lock className="w-4 h-4 mr-1.5" />Activate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Security Code Modal */}
      <Dialog open={showChangeCode} onOpenChange={setShowChangeCode}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm" data-testid="change-code-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <KeyRound className="w-5 h-5" /> Change Security Code
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-400">This will immediately change the portal access code. All staff will need the new code to access the login page.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">New Security Code</label>
              <input type="text" value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                data-testid="new-code-input" maxLength={12}
                className="w-full px-4 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white font-mono text-lg tracking-widest placeholder-slate-600 focus:border-amber-500/40 focus:outline-none uppercase"
                placeholder="MIN 6 CHARS" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Your Password (Confirm)</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="confirm-password-input"
                className="w-full px-4 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:border-amber-500/40 focus:outline-none text-sm"
                placeholder="Enter your password" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={() => { setShowChangeCode(false); setNewCode(''); setConfirmPassword(''); }}
                variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400 hover:bg-slate-800">Cancel</Button>
              <Button onClick={handleChangeCode} size="sm" data-testid="confirm-change-code"
                className="flex-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/20"
                disabled={newCode.length < 6 || !confirmPassword}>
                <KeyRound className="w-4 h-4 mr-1.5" /> Update Code
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Force Password Reset Confirmation */}
      <Dialog open={showForceReset} onOpenChange={setShowForceReset}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm" data-testid="force-reset-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" /> Force Password Reset
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <p className="text-sm text-slate-400">
              This will flag <span className="text-white font-medium">all non-superadmin staff accounts</span> to require a password change on their next login.
            </p>
            <div className="flex gap-2 pt-1">
              <Button onClick={() => setShowForceReset(false)}
                variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400">Cancel</Button>
              <Button onClick={handleForceResetAll} size="sm" data-testid="confirm-force-reset"
                className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20">
                <RotateCcw className="w-4 h-4 mr-1.5" /> Force Reset
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invalidate Sessions Confirmation */}
      <Dialog open={showInvalidateSessions} onOpenChange={setShowInvalidateSessions}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm" data-testid="invalidate-sessions-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" /> Invalidate All Sessions
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <p className="text-sm text-slate-400">
              This will force <span className="text-white font-medium">all staff members</span> to re-login. Use this if you suspect unauthorized access.
            </p>
            <div className="flex gap-2 pt-1">
              <Button onClick={() => setShowInvalidateSessions(false)}
                variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400">Cancel</Button>
              <Button onClick={handleInvalidateSessions} size="sm" data-testid="confirm-invalidate"
                className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20">
                <LogOut className="w-4 h-4 mr-1.5" /> Invalidate All
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Step-Up Auth Modal ===== */}
      <Dialog open={showStepUp} onOpenChange={(o) => { if (!o) { setShowStepUp(false); setPendingAction(null); setStepUpPassword(''); setStepUpError(''); } }}>
        <DialogContent className="bg-slate-900 border-red-500/30 max-w-md" data-testid="stepup-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-400" />
              Re-verify Your Identity
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitStepUp} className="space-y-4 pt-2">
            <p className="text-slate-400 text-sm">
              Destructive Security Centre actions require fresh password verification. This is a defence layer to stop session-hijack attacks from being able to wreck your security setup.
            </p>
            {stepUpError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-400 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {stepUpError}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">Your Superadmin Password</label>
              <input type="password" value={stepUpPassword} autoFocus data-testid="stepup-password"
                onChange={(e) => setStepUpPassword(e.target.value)}
                className="w-full px-3 py-2.5 bg-black/40 border border-slate-700 rounded-lg text-white focus:border-red-500/50 focus:outline-none" />
            </div>
            <div className="bg-slate-800/40 rounded-lg p-2.5 text-[11px] text-slate-500 flex items-start gap-2">
              <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-emerald-400" />
              <span>Verification grants a 10-minute, 20-action token. After that you'll be asked again. Token is tied to your IP and account.</span>
            </div>
            <div className="flex gap-2">
              <Button type="button" onClick={() => { setShowStepUp(false); setPendingAction(null); setStepUpPassword(''); }}
                variant="outline" className="flex-1 border-slate-700 text-slate-400 hover:text-white">
                Cancel
              </Button>
              <Button type="submit" disabled={stepUpProcessing || !stepUpPassword} data-testid="stepup-submit"
                className="flex-1 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-400 hover:to-rose-500 text-white">
                {stepUpProcessing ? 'Verifying…' : 'Verify & Continue'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityManagementPage;
